import { Muxer, ArrayBufferTarget } from 'mp4-muxer'
import { GIFEncoder } from './gif-encoder'

export interface RecordingOptions {
  width: number
  height: number
  framerate: number
  duration: number // in seconds
  format: 'mp4' | 'gif'
}

export interface RecordingProgress {
  frame: number
  totalFrames: number
  percentage: number
  timeRemaining?: number
}

export class CanvasRecorder {
  private canvas: HTMLCanvasElement
  private muxer?: Muxer<ArrayBufferTarget>
  private encoder?: VideoEncoder
  private isRecording = false
  private startTime = 0
  private totalFrames = 0

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
  }

  async startRecording(
    options: RecordingOptions,
    onProgress?: (progress: RecordingProgress) => void
  ): Promise<ArrayBuffer> {
    if (this.isRecording) {
      throw new Error('Already recording')
    }

    this.isRecording = true
    this.totalFrames = Math.ceil(options.duration * options.framerate)
    this.startTime = performance.now()

    return new Promise(async (resolve, reject) => {
      try {
        if (options.format === 'mp4') {
          const result = await this.recordMP4(options, onProgress)
          resolve(result)
        } else {
          // For GIF, we'll use a simpler approach
          const result = await this.recordGIF(options, onProgress)
          resolve(result)
        }
      } catch (error) {
        reject(error)
      } finally {
        this.isRecording = false
      }
    })
  }

  private async recordMP4(
    options: RecordingOptions,
    onProgress?: (progress: RecordingProgress) => void
  ): Promise<ArrayBuffer> {
    // Create MP4 muxer
    this.muxer = new Muxer({
      target: new ArrayBufferTarget(),
      fastStart: 'in-memory',
      video: {
        codec: 'avc',
        width: options.width,
        height: options.height,
        frameRate: options.framerate
      }
    })

    // Check for WebCodecs support
    if ('VideoEncoder' in window) {
      return this.recordWithWebCodecs(options, onProgress)
    } else {
      return this.recordWithMediaRecorder(options, onProgress)
    }
  }

  private async recordWithWebCodecs(
    options: RecordingOptions,
    onProgress?: (progress: RecordingProgress) => void
  ): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      let encodedFrames = 0
      
      this.encoder = new VideoEncoder({
        output: (chunk, metadata) => {
          this.muxer!.addVideoChunk(chunk, metadata)
          encodedFrames++
          
          if (encodedFrames >= this.totalFrames) {
            this.muxer!.finalize()
            const buffer = (this.muxer!.target as ArrayBufferTarget).buffer
            resolve(buffer!)
          }
        },
        error: reject
      })

      // Use appropriate codec level based on resolution
      let codecString = 'avc1.42E01E' // Level 3.0 (default)
      const pixelCount = options.width * options.height
      
      if (pixelCount > 2073600) {
        codecString = 'avc1.640028' // Level 4.0 for 4K
      } else if (pixelCount > 921600) {
        codecString = 'avc1.640028' // Level 4.0 for 1080p (safer than 3.1)
      } else if (pixelCount > 414720) {
        codecString = 'avc1.4D401F' // Level 3.1 for 720p
      }
      
      try {
        this.encoder.configure({
          codec: codecString,
          width: options.width,
          height: options.height,
          framerate: options.framerate,
          bitrate: Math.max(2000000, options.width * options.height * 0.1) // Dynamic bitrate
        })

        this.captureFrames(options, onProgress)
      } catch (error) {
        console.error('WebCodecs configuration failed:', error)
        // Fallback to MediaRecorder
        reject(error)
      }
    })
  }

  private async recordWithMediaRecorder(
    options: RecordingOptions,
    onProgress?: (progress: RecordingProgress) => void
  ): Promise<ArrayBuffer> {
    // Fallback to MediaRecorder API
    const stream = this.canvas.captureStream(options.framerate)
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: 'video/webm;codecs=vp9'
    })

    const chunks: Blob[] = []

    return new Promise((resolve, reject) => {
      mediaRecorder.ondataavailable = (event) => {
        chunks.push(event.data)
      }

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'video/webm' })
        const buffer = await blob.arrayBuffer()
        resolve(buffer)
      }

      mediaRecorder.onerror = reject

      mediaRecorder.start()
      
      // Capture frames and stop after duration
      setTimeout(() => {
        mediaRecorder.stop()
      }, options.duration * 1000)

      this.captureFrames(options, onProgress)
    })
  }

  private async recordGIF(
    options: RecordingOptions,
    onProgress?: (progress: RecordingProgress) => void
  ): Promise<ArrayBuffer> {
    // Create GIF encoder
    const encoder = new GIFEncoder({
      width: options.width,
      height: options.height,
      framerate: options.framerate,
      quality: 10 // Lower is better quality
    })

    // Calculate delay between frames in milliseconds
    const frameDelay = 1000 / options.framerate

    // Capture frames
    for (let frame = 0; frame < this.totalFrames; frame++) {
      const currentTime = (frame / options.framerate)
      
      // Trigger frame rendering at specific time
      await this.renderFrameAtTime(currentTime)
      
      // Add frame to GIF encoder
      encoder.addFrame(this.canvas, frameDelay)
      
      // Update progress
      const progress: RecordingProgress = {
        frame: frame + 1,
        totalFrames: this.totalFrames,
        percentage: ((frame + 1) / this.totalFrames) * 100,
        timeRemaining: this.estimateTimeRemaining(frame + 1)
      }
      
      onProgress?.(progress)

      // Wait for next frame
      await new Promise(resolve => setTimeout(resolve, 16)) // ~60fps for smooth capture
    }

    // Render the GIF
    return encoder.render()
  }

  private async captureFrames(
    options: RecordingOptions,
    onProgress?: (progress: RecordingProgress) => void
  ): Promise<void> {
    for (let frame = 0; frame < this.totalFrames; frame++) {
      const currentTime = (frame / options.framerate)
      
      // Trigger frame rendering at specific time
      await this.renderFrameAtTime(currentTime)
      
      if (this.encoder && this.encoder.state !== 'closed') {
        try {
          // Capture frame for WebCodecs
          const videoFrame = new VideoFrame(this.canvas, {
            timestamp: frame * (1000000 / options.framerate) // microseconds
          })
          
          this.encoder.encode(videoFrame)
          videoFrame.close()
        } catch (error) {
          console.error('Failed to encode frame:', error)
          // Stop capturing if encoder fails
          break
        }
      }

      // Update progress
      const progress: RecordingProgress = {
        frame: frame + 1,
        totalFrames: this.totalFrames,
        percentage: ((frame + 1) / this.totalFrames) * 100,
        timeRemaining: this.estimateTimeRemaining(frame + 1)
      }
      
      onProgress?.(progress)

      // Wait for next frame
      await new Promise(resolve => setTimeout(resolve, 16)) // ~60fps for smooth capture
    }

    if (this.encoder) {
      await this.encoder.flush()
    }
  }

  private async renderFrameAtTime(time: number): Promise<void> {
    // This should trigger the SplatViewer to render at specific time
    // We'll dispatch a custom event that the parent component can listen to
    const event = new CustomEvent('renderAtTime', { detail: { time } })
    window.dispatchEvent(event)  // Dispatch to window instead of canvas
    
    // Wait for frame to be rendered
    await new Promise(resolve => requestAnimationFrame(resolve))
  }

  private estimateTimeRemaining(currentFrame: number): number {
    const elapsed = performance.now() - this.startTime
    const framesPerMs = currentFrame / elapsed
    const remainingFrames = this.totalFrames - currentFrame
    return remainingFrames / framesPerMs
  }


  stopRecording(): void {
    this.isRecording = false
    if (this.encoder) {
      this.encoder.close()
      this.encoder = undefined
    }
    if (this.muxer) {
      this.muxer.finalize()
    }
  }

  get recording(): boolean {
    return this.isRecording
  }
}